import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokensAPI } from '../../api/tokens';
import { userAPI } from '../../api/user';
import { sessionsAPI } from '../../api/sessions';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  formatDate,
  getTokenStatusColor,
  formatBytes,
  formatDuration,
  formatTraffic,
  getSessionStatusColor,
  getSessionStatusText
} from '../../utils/format';

const Dashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    revoked: 0
  });
  const [recentTokens, setRecentTokens] = useState([]);
  const [limits, setLimits] = useState({
    maxPorts: 3,
    usedPorts: 0,
    availablePorts: 3
  });
  const [trafficStats, setTrafficStats] = useState({
    totalBytesUsed: 0,
    monthlyBytesUsed: 0
  });
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Загружаем все токены для статистики, лимиты, статистику трафика и активные сессии параллельно
      const [allTokensData, limitsData, trafficStatsData, activeSessionsResponse] = await Promise.all([
        tokensAPI.getList({ includeExpired: true }),
        userAPI.getLimits(),
        userAPI.getStats(),
        sessionsAPI.getSessions({ status: 'active', limit: 10 })
      ]);

      const allTokens = allTokensData.tokens || [];

      // Подсчитываем статистику по ВСЕМ токенам
      const statsData = {
        total: allTokens.length,
        active: allTokens.filter(t => t.status === 'active').length,
        expired: allTokens.filter(t => t.status === 'expired').length,
        revoked: allTokens.filter(t => t.status === 'revoked').length
      };

      setStats(statsData);
      setRecentTokens(allTokens.slice(0, 5)); // Последние 5 токенов для отображения
      setLimits(limitsData);

      // Устанавливаем статистику трафика
      if (trafficStatsData && trafficStatsData.stats) {
        setTrafficStats(trafficStatsData.stats);
      }

      // Устанавливаем активные сессии
      if (activeSessionsResponse && activeSessionsResponse.data && activeSessionsResponse.data.sessions) {
        setActiveSessions(activeSessionsResponse.data.sessions);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Безопасное получение числового значения
  const getSafeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? 0 : num;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        <p className="mt-2 text-gray-600">{t('dashboard.welcome')}</p>
      </div>

      {/* Карточки статистики */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Всего токенов */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('dashboard.stats.total')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Активных */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('dashboard.stats.active')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.active}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Истекших */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('dashboard.stats.expired')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.expired}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Отозванных */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('dashboard.stats.revoked')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.revoked}</p>
            </div>
            <div className="bg-gray-100 p-3 rounded-full">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Лимиты портов */}
      <div className="bg-white rounded-lg shadow p-6 border-l-4 border-indigo-500">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.portLimits.title')}</h3>
          <div className="bg-indigo-100 p-2 rounded-lg">
            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
            </svg>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{limits.maxPorts}</div>
            <div className="text-sm text-gray-600 mt-1">{t('dashboard.portLimits.maxPorts')}</div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{limits.usedPorts}</div>
            <div className="text-sm text-gray-600 mt-1">{t('dashboard.portLimits.usedPorts')}</div>
          </div>
          
          <div className="text-center">
            <div className={`text-3xl font-bold ${limits.availablePorts > 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {limits.availablePorts}
            </div>
            <div className="text-sm text-gray-600 mt-1">{t('dashboard.portLimits.availablePorts')}</div>
          </div>
        </div>
        
        {limits.availablePorts === 0 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">
              {t('dashboard.portLimits.limitReached')}
            </p>
          </div>
        )}
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>{t('dashboard.portLimits.usage')}</span>
            <span>{limits.usedPorts} / {limits.maxPorts}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full ${
                limits.usedPorts >= limits.maxPorts
                  ? 'bg-red-600'
                  : limits.usedPorts >= limits.maxPorts * 0.8
                  ? 'bg-yellow-500'
                  : 'bg-blue-600'
              }`}
              style={{ width: `${(limits.usedPorts / limits.maxPorts) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Статистика трафика */}
      <div className="bg-white rounded-lg shadow p-6 border-l-4 border-cyan-500">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.trafficStats.title')}</h3>
          <div className="bg-cyan-100 p-2 rounded-lg">
            <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-cyan-600">{formatBytes(trafficStats.totalBytesUsed)}</div>
            <div className="text-sm text-gray-600 mt-1">{t('dashboard.trafficStats.totalUsed')}</div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{formatBytes(trafficStats.monthlyBytesUsed)}</div>
            <div className="text-sm text-gray-600 mt-1">{t('dashboard.trafficStats.monthlyUsed')}</div>
          </div>
        </div>
      </div>

      {/* Активные сессии */}
      <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">{t('dashboard.activeSessions.title')}</h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">{activeSessions.length} {t('dashboard.activeSessions.activeCount')}</span>
            <div className="bg-green-100 p-2 rounded-lg">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
        </div>
        
        {activeSessions.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <p className="mt-2 text-sm text-gray-600">{t('dashboard.activeSessions.noSessions')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.activeSessions.proxy')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.activeSessions.port')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.activeSessions.client')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.activeSessions.protocol')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.activeSessions.connected')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.activeSessions.duration')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.activeSessions.traffic')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activeSessions.map((session) => (
                  <tr key={session.id} className="hover:bg-green-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-2">
                        <div className="bg-green-100 p-1 rounded">
                          <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                          </svg>
                        </div>
                        <span className="font-medium">{session.proxyId || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs">{session.port || '-'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-xs">{session.clientAddress || '-'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant={session.protocol === 'tcp' ? 'blue' : 'purple'} size="sm">
                        {session.protocol?.toUpperCase() || '-'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(session.connectedAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <span className="font-medium">{formatDuration(session.duration)}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <span className="text-green-600">↑{formatBytes(getSafeNumber(session.bytesSent))}</span>
                        <span className="text-gray-300">/</span>
                        <span className="text-blue-600">↓{formatBytes(getSafeNumber(session.bytesReceived))}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Последние токены */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('dashboard.recentTokens.title')}</h2>
        {recentTokens.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <p className="mt-2 text-sm text-gray-600">{t('dashboard.recentTokens.noTokens')}</p>
            <Link to="/tokens" className="mt-4 inline-block">
              <Button size="sm">{t('dashboard.recentTokens.createFirst')}</Button>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.recentTokens.proxy')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.recentTokens.ports')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.recentTokens.created')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.recentTokens.status')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentTokens.map((token) => (
                  <tr key={token.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {token.proxyId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {token.ports && token.ports.length > 0 
                        ? `${token.ports.length} ${t('dashboard.recentTokens.portsCount')}` 
                        : t('dashboard.recentTokens.portsAny')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(token.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={getTokenStatusColor(token.status)}>
                        {token.status === 'active' ? t('dashboard.recentTokens.statusActive') : 
                         token.status === 'expired' ? t('dashboard.recentTokens.statusExpired') : t('dashboard.recentTokens.statusRevoked')}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
